import { Icon, Modal, Skeleton, TextField, Typography } from '@mui/material'
import { Box } from '@mui/system'
import InterviewCandidatePopper from './InterviewCandidatePopper'
import useOpenClose from 'services/hooks/useOpenClose'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { editInterview } from 'services/api/modules/recruitment/scheduler'
import { useState } from 'react'
import { isPast, isToday } from 'date-fns'
import CalendarDetailsModal from '../../recruitment-talent-calendar/components/CalendarDetailsModal'
import { getInterviewSlot } from 'services/api/modules/recruitment/external-talent'
import AdxButton from 'components/custom/AdxButton'

const InterviewTag = ({ id, interviewId }) => {
  const navigate = useNavigate()

  //modal states
  const noShowInterviewModalState = useOpenClose(false)
  const cancelInterviewModalState = useOpenClose(false)
  const completeInterviewModalState = useOpenClose(false)

  //api call
  const queryClient = useQueryClient()
  const { data: interviewData, isLoading: interviewDataIsLoading } = useQuery(
    ['getInterviewSlot', interviewId],
    async () => {
      const { data } = await getInterviewSlot(interviewId)
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  const candidateData = interviewData?.relations?.jobAdCandidate?.relations?.candidateable

  const {
    mutate: editInterviewMutate,
    isLoading: editInterviewIsLoading,
    isError: editInterviewIsError,
  } = useMutation((payload) => editInterview(interviewData?.id, payload), {
    onSuccess: async (response) => {
      await queryClient.invalidateQueries('getInterviewSlot')
      console.log(response)
      if (
        noShowInterviewModalState.isOpen ||
        cancelInterviewModalState.isOpen ||
        completeInterviewModalState.isOpen
      ) {
        noShowInterviewModalState.handleClose()
        cancelInterviewModalState.handleClose()
        completeInterviewModalState.handleClose()
      }
    },
    onError: (error) => {
      console.log(error)
    },
  })

  // popper
  const [open, setOpen] = useState(false)
  const handleToggle = () => {
    setOpen((prev) => !prev)
  }

  // check if date has passed
  const checkIsPast = (date) => {
    return isPast(date) && !isToday(date)
  }
  const endDateObject = new Date(interviewData?.end_date)
  const pendingStatus = checkIsPast(endDateObject)
  const convertDate = (date) => {
    const parts = date?.split('-')
    const interviewStartDate = `${parts[2]}/${parts[1]}/${parts[0]}`
    return interviewStartDate
  }
  const convertTimeTo12HourFormat = (time24) => {
    const [hours, minutes, seconds] = time24?.split(':')
    let period = 'AM'
    let hours12 = parseInt(hours, 10)

    if (hours12 >= 12) {
      period = 'PM'
      if (hours12 > 12) {
        hours12 -= 12
      }
    } else if (hours12 === 0) {
      hours12 = 12 // Midnight (12:00 AM)
    }

    const time12 = `${hours12}:${minutes} ${period}`
    return time12
  }
  if (interviewDataIsLoading) {
    return <Skeleton variant="rectangle" height={'50px'} width={'100%'} />
  } else {
    return (
      <>
        {interviewData?.status === 'scheduled' && !pendingStatus && (
          <Box
            px={2}
            height={'50px'}
            bgcolor="gray-background.main"
            display="flex"
            alignItems="center"
            columnGap={2}
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" columnGap={2}>
              <Icon
                sx={{
                  color: 'light-gray-text.main',
                }}
              >
                calendar_month
              </Icon>
              <Typography variant="body1" color="light-gray-text.main">
                {`Interview was scheduled on ${convertDate(
                  interviewData?.start_date
                )}, ${convertTimeTo12HourFormat(interviewData?.start_time)}`}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" columnGap={2}>
              <Typography
                variant="body1"
                color="alternate.main"
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() =>
                  navigate('/app/recruitment/schedule-interview/edit', {
                    state: {
                      is_edit: true,
                      ...interviewData,
                    },
                  })
                }
              >
                Edit
              </Typography>
              <InterviewCandidatePopper
                endDateObject={endDateObject}
                interviewData={interviewData}
                cancelInterviewModal={cancelInterviewModalState.handleOpen}
                isLoading={interviewDataIsLoading}
              />
            </Box>
            {cancelInterviewModalState.isOpen && (
              <CancelInterviewModal
                open={cancelInterviewModalState.isOpen}
                onClose={cancelInterviewModalState.handleClose}
                isLoading={editInterviewIsLoading}
                onClick={(remarks) => editInterviewMutate({ status: 2, remarks: remarks })}
                isError={editInterviewIsError}
              />
            )}
          </Box>
        )}
        {interviewData?.status === 'scheduled' && pendingStatus && (
          <Box
            px={2}
            height={'50px'}
            bgcolor="gray-background.main"
            display="flex"
            alignItems="center"
            columnGap={2}
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" columnGap={2}>
              <Icon
                sx={{
                  color: 'yellow-01.main',
                }}
              >
                error
              </Icon>
              <Typography variant="body1" color="light-gray-text.main">
                The interview session has passed.
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" columnGap={2}>
              <Typography
                variant="body1"
                color="alternate.main"
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                onClick={handleToggle}
              >
                View details
              </Typography>
              <InterviewCandidatePopper
                endDateObject={endDateObject}
                interviewData={interviewData}
                cancelInterviewModal={cancelInterviewModalState.handleOpen}
                completeInterviewModal={completeInterviewModalState.handleOpen}
                noShowInterviewModal={noShowInterviewModalState.handleOpen}
                type="pending"
                isLoading={interviewDataIsLoading}
              />
            </Box>
            {noShowInterviewModalState.isOpen && (
              <NoShowInterviewModal
                open={noShowInterviewModalState.isOpen}
                onClose={noShowInterviewModalState.handleClose}
                isLoading={editInterviewIsLoading}
                onClick={(remarks) => editInterviewMutate({ status: 4, remarks: remarks })}
                isError={editInterviewIsError}
              />
            )}
            {cancelInterviewModalState.isOpen && (
              <CancelInterviewModal
                open={cancelInterviewModalState.isOpen}
                onClose={cancelInterviewModalState.handleClose}
                isLoading={editInterviewIsLoading}
                onClick={(remarks) => editInterviewMutate({ status: 2, remarks: remarks })}
                isError={editInterviewIsError}
              />
            )}
            {completeInterviewModalState.isOpen && (
              <CompleteInterviewModal
                open={completeInterviewModalState.isOpen}
                onClose={completeInterviewModalState.handleClose}
                isLoading={editInterviewIsLoading}
                onClick={() => editInterviewMutate({ status: 3 })}
                isError={editInterviewIsError}
              />
            )}
          </Box>
        )}
        {interviewData?.status === 'completed' && (
          <Box
            px={2}
            height={'50px'}
            bgcolor="gray-background.main"
            display="flex"
            alignItems="center"
            columnGap={2}
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" columnGap={2}>
              <Icon
                sx={{
                  color: 'positive.main',
                }}
              >
                check_circle
              </Icon>
              <Typography variant="body1" color="light-gray-text.main">
                {`Interview was completed on ${convertDate(
                  interviewData?.end_date
                )}, ${convertTimeTo12HourFormat(interviewData?.end_time)}`}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" columnGap={2}>
              <Typography
                variant="body1"
                color="alternate.main"
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => {
                  navigate('/app/recruitment/schedule-interview', {
                    state: {
                      applicant_name: candidateData?.name,
                      email: candidateData?.email,
                      id,
                      profile_picture: candidateData?.profile_image_url,
                    },
                  })
                }}
              >
                Reinterview
              </Typography>
              <InterviewCandidatePopper
                endDateObject={endDateObject}
                candidateData={candidateData}
                id={id}
                interviewData={interviewData}
                cancelInterviewModal={cancelInterviewModalState.handleOpen}
                type="reinterview"
                isLoading={interviewDataIsLoading}
              />
            </Box>
          </Box>
        )}
        {interviewData?.status === 'cancelled' && (
          <Box
            px={2}
            height={'50px'}
            bgcolor="gray-background.main"
            display="flex"
            alignItems="center"
            columnGap={2}
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" columnGap={2}>
              <Icon
                sx={{
                  color: 'error.main',
                }}
              >
                cancel
              </Icon>
              <Typography variant="body1" color="light-gray-text.main">
                Interview was canceled
              </Typography>
            </Box>
            <Typography
              variant="body1"
              color="alternate.main"
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={handleToggle}
            >
              View details
            </Typography>
          </Box>
        )}
        {interviewData?.status === 'candidate-no-show' && (
          <Box
            px={2}
            height={'50px'}
            bgcolor="gray-background.main"
            display="flex"
            alignItems="center"
            columnGap={2}
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" columnGap={2}>
              <Icon
                sx={{
                  color: 'warning.main',
                }}
              >
                help_outline
              </Icon>
              <Typography variant="body1" color="light-gray-text.main">
                Candidate-no-show
              </Typography>
            </Box>
            <Typography
              variant="body1"
              color="alternate.main"
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={handleToggle}
            >
              View details
            </Typography>
          </Box>
        )}
        <CalendarDetailsModal
          open={open}
          handleClose={handleToggle}
          data={interviewData}
          dateData={endDateObject}
          interviewSlotIsLoading={interviewDataIsLoading}
        />
      </>
    )
  }
}
export default InterviewTag

const NoShowInterviewModal = ({ open, onClose, isLoading, onClick, isError }) => {
  const [textLength, setTextLength] = useState(0)
  const [remarks, setRemarks] = useState('')

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '600px',
    width: '100%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '6.5px',
    padding: '40px 32px 32px 32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '40px',
    position: 'relative',
  }
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <Typography variant="h4" sx={{ width: '341px', textAlign: 'center' }}>
            Are you sure with the status?
          </Typography>
          <Typography variant="body1">Please add the reason of the candidate's absence.</Typography>
        </Box>
        <Box
          sx={{
            minWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              minWidth: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              padding: '16px',
              backgroundColor: 'gray-background.main',
              borderRadius: '4.5px',
            }}
          >
            <Icon sx={{ color: 'warning.main' }}>help_outline</Icon>
            <Typography sx={{ width: '100%' }}>Candidate no-show</Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              width: '100%',
              gap: '6px',
            }}
          >
            <TextField
              value={remarks}
              fullWidth
              sx={{ height: '137px' }}
              multiline={true}
              rows={5}
              inputProps={{ maxLength: 120 }}
              placeholder="Add any remark (Optional)"
              onChange={(e) => {
                setTextLength(e.target.value.length)
                setRemarks(e.target.value)
              }}
            />
            <Typography sx={{ textAlign: 'end' }}>{textLength}/120</Typography>
          </Box>
        </Box>
        <Box sx={{ width: '100%' }} display="flex" flexDirection="column" columnGap={2}>
          <AdxButton
            variant={'contained'}
            sx={{
              boxShadow: 'none',
              backgroundColor: 'alternate.main',
              '&:hover': {
                backgroundColor: 'alternate.main',
                opacity: 0.9,
              },
            }}
            fullWidth
            isLoading={isLoading}
            onClick={() => onClick(remarks)}
          >
            Confirm
          </AdxButton>
          <AdxButton variant={'ghost'} sx={{ color: 'alternate.main' }} fullWidth onClick={onClose}>
            Cancel
          </AdxButton>
          {isError && (
            <Typography variant="body1" color="error.main">
              An error has occured. Please try again
            </Typography>
          )}
        </Box>
      </Box>
    </Modal>
  )
}
const CancelInterviewModal = ({ open, onClose, isLoading, onClick, isError }) => {
  const [textLength, setTextLength] = useState(0)
  const [remarks, setRemarks] = useState('')

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '600px',
    width: '100%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '6.5px',
    padding: '40px 32px 32px 32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '40px',
    position: 'relative',
  }
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <Typography variant="h4" sx={{ width: '341px', textAlign: 'center' }}>
            Do you confirm the cancellation of the interview session?
          </Typography>
          <Typography variant="body1">
            You may add a remark of the interview cancellation.
          </Typography>
        </Box>
        <Box
          sx={{
            minWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              minWidth: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              padding: '16px',
              backgroundColor: 'gray-background.main',
              borderRadius: '4.5px',
            }}
          >
            <Icon sx={{ color: 'error.main' }}>highlight_off</Icon>
            <Typography sx={{ width: '100%' }}>Interview cancelled</Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              width: '100%',
              gap: '6px',
            }}
          >
            <TextField
              value={remarks}
              fullWidth
              sx={{ height: '137px' }}
              multiline={true}
              rows={5}
              inputProps={{ maxLength: 120 }}
              placeholder="Add any remark (Optional)"
              onChange={(e) => {
                setTextLength(e.target.value.length)
                setRemarks(e.target.value)
              }}
            />
            <Typography sx={{ textAlign: 'end' }}>{textLength}/120</Typography>
          </Box>
        </Box>
        <Box sx={{ width: '100%' }} display="flex" flexDirection="column" columnGap={2}>
          <AdxButton
            variant={'contained'}
            sx={{
              boxShadow: 'none',
              backgroundColor: 'alternate.main',
              '&:hover': {
                backgroundColor: 'alternate.main',
                opacity: 0.9,
              },
            }}
            fullWidth
            isLoading={isLoading}
            onClick={() => onClick(remarks)}
          >
            Yes, cancel interview
          </AdxButton>
          <AdxButton variant={'ghost'} sx={{ color: 'alternate.main' }} fullWidth onClick={onClose}>
            No, keep interview
          </AdxButton>
          {isError && (
            <Typography variant="body1" color="error.main">
              An error has occured. Please try again
            </Typography>
          )}
        </Box>
      </Box>
    </Modal>
  )
}

const CompleteInterviewModal = ({ open, onClose, isLoading, onClick, isError }) => (
  <AdxInfoModal
    modalWidth={600}
    muiIcon="error"
    open={open}
    onClose={onClose}
    iconColor="warning.main"
    title="Do you confirm to mark this session as completed?"
    subtitle=""
    footerButtons
    firstBtnLabel={'Yes'}
    firstBtnHandle={onClick}
    secondBtnLabel={'Cancel'}
    secondBtnHandle={onClose}
  >
    {isError && (
      <Typography variant="body1" color="error.main">
        An error has occured. Please try again
      </Typography>
    )}
  </AdxInfoModal>
)
